/**
 * Server time on the client. `Date.now()` is never used for game time: it jumps when someone changes
 * the phone's clock. `performance.now()` is monotonic; `serverNow()` is the server's time at the last
 * sync plus what has elapsed since, corrected for half the round trip.
 */
export interface Clock {
  /** Server milliseconds now, or `null` before the first sync. */
  serverNow(): number | null;
  /** Server milliseconds now, falling back to the wall clock before the first sync. */
  serverNowOrWall(): number;
  /** Called with a pong: the server's time and the `performance.now()` the ping was sent at. */
  onPong(serverTime: number, sentAt: number): void;
  /** Milliseconds since the last sync, or `Infinity` before the first. */
  age(): number;
  /** Drops the sync, for example when the app resumes after a long sleep. */
  reset(): void;
}

export function createClock(now: () => number = () => performance.now()): Clock {
  let offset: number | null = null; // serverTime - performance.now() at the sync
  let bestRtt = Infinity;
  let syncedAt = -Infinity;
  return {
    serverNow: () => (offset === null ? null : now() + offset),
    serverNowOrWall() {
      // eslint-disable-next-line no-restricted-globals, no-restricted-syntax -- the one place the wall clock is a fallback
      return offset === null ? Date.now() : now() + offset;
    },
    onPong(serverTime, sentAt) {
      const received = now();
      const rtt = Math.max(0, received - sentAt);
      // A sample from a slower round trip replaces an old one only when the old one is stale (30 s).
      if (rtt > bestRtt && received - syncedAt < 30_000) return;
      offset = serverTime + rtt / 2 - received;
      bestRtt = rtt;
      syncedAt = received;
    },
    age: () => (offset === null ? Infinity : now() - syncedAt),
    reset() {
      offset = null;
      bestRtt = Infinity;
      syncedAt = -Infinity;
    },
  };
}
